import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import React from 'react';
import { GetVorgangKommentare200ResponseInner } from '@/adapter/opm-cockpit-backend/__generated';
import { VorgangKommentar } from '@/types';

interface VorgangKommentarTableProps {
  kommentare: GetVorgangKommentare200ResponseInner[];
}

export const VorgangKommentarTable: React.FC<VorgangKommentarTableProps> = ({
  kommentare,
}) => {
  const table = useReactTable({
    data: kommentare,
    columns: getKommentarColumns(),
    getCoreRowModel: getCoreRowModel(),
    renderFallbackValue: '-',
    manualPagination: true,
    manualSorting: true, // Enable server-side sorting
    pageCount: -1, // Unknown total pages
  });

  return (
    <div className="bg-white border border-table-border rounded-lg overflow-x-auto overflow-y-auto">
      <table className="w-full">
        {/* Table Header */}
        <thead className="bg-table-header border-b border-table-border border-l-4 border-l-table-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`text-left p-3 font-medium text-text-primary ${
                    header.id === 'select' ? 'w-12' : ''
                  }`}
                >
                  {header.isPlaceholder ? null : (
                    <div className={`flex items-center justify-start`}>
                      <div className="flex items-center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {/* Table Body */}
        <tbody>
          {table.getRowModel().rows.map((row) => {
            return (
              <tr
                key={row.id}
                className={`
                      border-transparent transition-colors
                      border-l-4
                    `}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="p-3 text-text-primary font-light text-sm"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const columnHelper = createColumnHelper<VorgangKommentar>();

const truncateKommentar = (kommentar: string, maxLength = 50) => {
  if (kommentar.length <= maxLength) {
    return kommentar;
  }

  return `${kommentar.slice(0, maxLength - 3)}...`;
};

const getKommentarColumns = () => {
  return [
    columnHelper.display({
      id: 'user',
      header: () => <th>Benutzer</th>,
      cell: ({ row }) => row.original.benutzer,
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.display({
      id: 'datum',
      header: () => <th>Datum</th>,
      cell: ({ row }) => {
        const formattedDate = format(
          new Date(row.original.datum),
          'dd.MM.yyyy HH:mm',
        );
        return <span className="text-text-secondary">{formattedDate}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.display({
      id: 'kommentar',
      header: () => <th>Kommentar</th>,
      cell: ({ row }) => {
        const kommentar = row.original.kommentar;

        return <span title={kommentar}>{truncateKommentar(kommentar)}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    }),
  ];
};
